import { useNavigate } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useDone } from '../hooks/useDone';
import { AppTopBar } from './AppTopBar';
import { SectionHeader } from './SectionHeader';
import { InternEmptyState, InternErrorState } from './shared/InternEmptyState';
import { DashboardSkeleton } from './shared/InternSkeletons';

export function DonePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userProfile } = usePermissions();
  const { tasks, skills, loading, error, refresh, reopenTask, reopenSkill } = useDone();

  const userName =
    userProfile?.display_name || user?.email?.split('@')[0] || 'there';

  const isEmpty = tasks.length === 0 && skills.length === 0;

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-warm-white)' }}>
      <AppTopBar userName={userName} />

      <main className="mx-auto max-w-[880px] px-6 py-10">
        <header className="mb-8">
          <h1
            className="font-serif font-[600] text-[36px] leading-none tracking-[-0.02em]"
            style={{ fontFamily: 'var(--font-serif)', color: 'var(--color-near-black)' }}
          >
            Done
          </h1>
          <p className="text-[15px] text-[var(--color-charcoal)] mt-2">
            Everything you've completed. Reopen anything that needs another pass.
          </p>
        </header>

        {loading && <DashboardSkeleton />}
        {error && <InternErrorState onRetry={refresh} />}

        {!loading && !error && isEmpty && (
          <InternEmptyState
            title="Nothing completed yet"
            body="Finish a task or explore a skill and it'll land here."
          />
        )}

        {!loading && !error && !isEmpty && (
          <div className="space-y-12">
            <section aria-label="Completed tasks">
              <SectionHeader
                title="Completed tasks"
                summary={tasks.length > 0 ? `${tasks.length}` : undefined}
              />
              {tasks.length === 0 ? (
                <p className="text-[14px] text-[var(--color-mid-gray)]">No completed tasks yet.</p>
              ) : (
                <div className="space-y-2">
                  {tasks.map((task) => (
                    <DoneRow
                      key={task.id}
                      title={task.title}
                      meta={task.completed_at ? `Completed ${formatDate(task.completed_at)}` : undefined}
                      onOpen={() => navigate(`/intern/task/${task.id}`)}
                      onReopen={() => reopenTask(task.id)}
                      reopenLabel="Reopen"
                    />
                  ))}
                </div>
              )}
            </section>

            <section aria-label="Explored skills">
              <SectionHeader
                title="Explored skills"
                summary={skills.length > 0 ? `${skills.length}` : undefined}
              />
              {skills.length === 0 ? (
                <p className="text-[14px] text-[var(--color-mid-gray)]">No explored skills yet.</p>
              ) : (
                <div className="space-y-2">
                  {skills.map((skill) => (
                    <DoneRow
                      key={skill.id}
                      title={skill.title}
                      meta={`Explored ${formatDate(skill.updated_at)}`}
                      onOpen={() => navigate(`/intern/skill/${skill.id}`)}
                      onReopen={() => reopenSkill(skill.id)}
                      reopenLabel="Revisit"
                    />
                  ))}
                </div>
              )}
            </section>

            <div className="h-16" aria-hidden />
          </div>
        )}
      </main>
    </div>
  );
}

function DoneRow({
  title,
  meta,
  onOpen,
  onReopen,
  reopenLabel,
}: {
  title: string;
  meta?: string;
  onOpen: () => void;
  onReopen: () => void;
  reopenLabel: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-[4px] border border-[var(--border)] bg-white px-4 py-3">
      <button
        type="button"
        onClick={onOpen}
        className="flex-1 min-w-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-gold)] rounded-[2px]"
      >
        <span className="text-[15px] font-[500] text-[var(--color-mid-gray)] line-through truncate block">
          {title}
        </span>
        {meta && <span className="text-[12px] text-[var(--color-mid-gray)]">{meta}</span>}
      </button>
      <button
        type="button"
        onClick={onReopen}
        className="flex items-center gap-1.5 shrink-0 text-[13px] font-[500] text-[var(--color-forest)] hover:underline"
      >
        <RotateCcw className="h-3.5 w-3.5" aria-hidden />
        {reopenLabel}
      </button>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
